import React, { Component } from 'react';
import { change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';

import styled from 'styled-components';
import Dropzone from 'react-dropzone';
import Lightbox from 'react-image-lightbox';

import { MdClose as CleanIcon } from 'react-icons/md';
import trashIcon from 'assets/trash-blue.png';

import { colors } from 'helpers/ColorHelper';

import { closeLightbox, openLightbox, setLightboxImage } from 'actions/dropzoneMultiFileActions';
import { featureEnabled } from 'helpers/ToggleBot';
import { getTranslatedString } from 'helpers/i18n';

const styles = {
  imagesContainer: {
    background: 'rgba(0,0,0,0.05)',
    border: '1px dashed rgba(0,0,0,0.1)',
    borderTop: 'none',
  },
  imageContainerHeader: {
    borderBottom: '1px dashed rgba(0,0,0,0.1)',
    fontSize: '11px',
    padding: '2px 5px',
  },
  imageContainerBody: {
    padding: '10px',
  },
  imageWrapper: {
    position: 'relative',
  },
  image: {
    marginRight: '5px',
    verticalAlign: 'top',
    width: '64px',
    border: '1px solid rgba(0,0,0,0.2)',
    height: '64px',
    fontSize: '11px',
    maxHeight: '64px',
    maxWidth: '64px',
    overflow: 'hidden',
    wordSpacing: 'nowrap',
    textOverflow: 'ellipsis',
    minHeight: '64px',
  },
  deleteBtn: {
    cursor: 'pointer',
    position: 'absolute',
    color: colors.BLACK,
    zIndex: '100',
    background: 'gainsboro',
    top: '0',
    left: '1px',
  },
  dropzone: {
    height: '100px',
    width: '100%',
    border: '1px dashed #e0e0e0',
    padding: '5px',
    cursor: 'pointer',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.68)',
    fontSize: '14px',
    padding: '10px',
  },
};

const StyledFileSizeErrorContainer = styled.div`
  color: ${colors.RED};
`;

class RenderDropzoneMultiFileInput extends Component {
  closeLightbox = () => {
    this.props.closeLightbox();
  };

  openLightbox = (image_number, file_type) => {
    if (!this.props.useLightbox || !file_type.startsWith('image')) {
      return;
    }
    this.props.openLightbox(image_number);
  };

  gotoPrevious = (image_number, imageArray) => {
    this.props.setLightboxImage((image_number + imageArray.length - 1) % imageArray.length);
  };

  gotoNext = (image_number, imageArray) => {
    this.props.setLightboxImage((image_number + 1) % imageArray.length);
  };

  deletePhotoFromForm = (event, id) => {
    event.preventDefault();
    let newPhotos = [...this.props.uploadedFiles];
    let targetIndex = newPhotos.findIndex((photo) => photo.id === id);
    newPhotos[targetIndex] = {
      ...newPhotos[targetIndex],
      keep: false,
    };
    this.props.changeFormValue(this.props.formName, this.props.uploadedFilesKey, newPhotos);
  };

  render() {
    return (
      <div>
        {this._renderDropZone()}
        {this._renderSelectedFiles()}
        {this._renderUploadedFiles()}
        {this._renderSizeLimit()}
      </div>
    );
  }

  _renderSizeLimit = () => {
    if (!featureEnabled('maintenance.max_image_upload')) return null;
    const field = this.props;
    const uploadedFiles = field.uploadedFiles;
    const newFiles = !this.props.hideDropzone ? field.input.value : '';
    let totalSize = 0;
    if (uploadedFiles && Array.isArray(uploadedFiles)) {
      totalSize = uploadedFiles.reduce(
        (sum, current) => sum + (current.keep ? current.size : 0),
        totalSize
      );
    }
    if (newFiles && Array.isArray(newFiles)) {
      totalSize = newFiles.reduce((sum, current) => sum + current.size, totalSize);
    }
    if (totalSize < this.props.imageMaxSize * 1000000) return null;
    return (
      <StyledFileSizeErrorContainer>
        {getTranslatedString('dropzone.filesTooLarge', { maxFileSize: this.props.imageMaxSize })}
      </StyledFileSizeErrorContainer>
    );
  };

  _renderDropZone = () => {
    if (this.props.hideDropzone) return null;

    const label =
      this.props.label ||
      this.props.translate('dropzone.ClickToUpload', { imageMaxSize: this.props.imageMaxSize });
    const field = this.props;

    const onDrop = (filesToUpload, e) => {
      let images = field.input.value || [];
      field.input.onChange(images.concat(filesToUpload));
    };

    return (
      <div>
        <Dropzone
          style={styles.dropzone}
          name={field.name}
          onDrop={onDrop}
          accept={this.props.acceptedFileTypes}
          disabled={this.props.readOnly}
          maxSize={this.props.imageMaxSize * 1000000}
        >
          <div style={styles.label} aria-label={label}>
            {label}
          </div>
        </Dropzone>
        {field.meta.touched && field.meta.error && (
          <span className="error">{field.meta.error}</span>
        )}
      </div>
    );
  };

  _renderSelectedFiles = () => {
    const field = this.props;
    const files = !this.props.hideDropzone ? field.input.value : '';
    if (this.props.hideDropzone || !files || !Array.isArray(files) || files.length === 0)
      return null;

    const clearFile = (files, file_name) => {
      files = files.filter((file) => {
        return file.name !== file_name;
      });
      field.input.onChange(files);
    };

    return (
      <div style={styles.imagesContainer}>
        <div style={styles.imageContainerHeader}>
          {this.props.translate('dropzone.notYetSavedItems').props.children}
        </div>
        <div style={styles.imageContainerBody}>
          {files.map((file) => (
            <span style={styles.imageWrapper}>
              <CleanIcon
                onClick={(event) => {
                  event.preventDefault();
                  clearFile(files, file.name);
                }}
                size={18}
                style={styles.deleteBtn}
              />
              <img alt={file.name} key={file.name} src={file.preview} style={styles.image} />
            </span>
          ))}
        </div>
      </div>
    );
  };

  _renderUploadedFiles = () => {
    let uploadedFiles = this.props.uploadedFiles || [];
    uploadedFiles = uploadedFiles.filter((file) => file.keep);

    if (!uploadedFiles || !Array.isArray(uploadedFiles) || uploadedFiles.length === 0) return null;

    const previewFilesArray = uploadedFiles.filter((file) => {
      return file.file_type && file.file_type.startsWith('image');
    });
    const uploadedFilesArray = previewFilesArray.map((file, _index) => file.src);

    return (
      <div style={{ display: 'inline-block', width: '100%' }}>
        <div style={styles.imagesContainer}>
          <div style={styles.imageContainerHeader}>
            {this.props.translate('dropzone.savedItems').props.children}
          </div>
          <div style={styles.imageContainerBody}>
            {this.props.lightboxIsOpen && (
              <Lightbox
                prevSrc={
                  uploadedFilesArray[
                    (this.props.currentImage + uploadedFilesArray.length - 1) %
                      uploadedFilesArray.length
                  ]
                }
                nextSrc={
                  uploadedFilesArray[(this.props.currentImage + 1) % uploadedFilesArray.length]
                }
                onCloseRequest={this.closeLightbox}
                mainSrc={uploadedFilesArray[this.props.currentImage]}
                onMoveNextRequest={() => this.gotoNext(this.props.currentImage, uploadedFilesArray)}
                onMovePrevRequest={() =>
                  this.gotoPrevious(this.props.currentImage, uploadedFilesArray)
                }
              />
            )}
            {!this.props.useViewUploadedFileLinks &&
              uploadedFiles.map((file, index) => (
                <span style={styles.imageWrapper}>
                  {!this.props.hideDropzone && (
                    <CleanIcon
                      onClick={(event) =>
                        this.props.readOnly ? null : this.deletePhotoFromForm(event, file.id)
                      }
                      size={18}
                      style={styles.deleteBtn}
                    />
                  )}
                  <img
                    alt={file.name}
                    onClick={() => this.openLightbox(index, file.file_type)}
                    key={file.src}
                    src={file.src}
                    style={styles.image}
                  />
                </span>
              ))}
            {this.props.useViewUploadedFileLinks &&
              uploadedFiles.map((file, index) => (
                <div key={`uploaded-link-${file.name}`} className="row">
                  <div className="row">
                    <div className="col-sm-6">
                      <a href={file.src} target="_blank" rel="noopener noreferrer">
                        {this.props.viewFileName
                          ? file.name
                          : this.props.translate('dropzone.viewFile').props.children}
                      </a>
                    </div>
                    <div className="col-sm-6">
                      {this.props.showDeleteButton && (
                        <img
                          alt="note_icon"
                          style={{ width: '21px', cursor: 'pointer', float: 'right' }}
                          src={trashIcon}
                          onClick={(event) => this.deletePhotoFromForm(event, file.id)}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <hr style={{ margin: '.10em auto .15em auto', width: '95%' }} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };
}

const connectedComponent = connect(
  (store, ownProps) => ({
    imageMaxSize: store.userTypes.imageMaxSize,
    currentImage: store.dropzoneMultiLine.currentImage,
    lightboxIsOpen: store.dropzoneMultiLine.lightboxIsOpen,
    uploadedFiles:
      (ownProps.formName &&
        ownProps.uploadedFilesKey &&
        formValueSelector(ownProps.formName)(store, ownProps.uploadedFilesKey)) ||
      ownProps.uploadedFiles,
  }),
  {
    closeLightbox,
    openLightbox,
    setLightboxImage,
    changeFormValue: change,
  }
)(RenderDropzoneMultiFileInput);

export default localize()(connectedComponent);
